import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { MapPin, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const AddressAutocomplete = ({
  id,
  label,
  value = '',
  onChange,
  onValidationChange,
  error,
  required = false,
  helpText,
  placeholder = 'הזן כתובת',
  className = '',
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Update input value when value prop changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced autocomplete search
  const searchAddresses = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(API_ENDPOINTS.ADDRESSES_AUTOCOMPLETE, {
        params: { query: query.trim() }
      });

      if (response.data.success && response.data.data.suggestions) {
        setSuggestions(response.data.data.suggestions);
        setShowSuggestions(true);
        setFocusedIndex(-1);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsValid(false);
    setSelectedAddress(null);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Call onChange immediately for react-hook-form
    if (onChange) {
      onChange({
        target: {
          name: id,
          value: newValue
        }
      });
    }

    // Debounce autocomplete search
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      searchAddresses(newValue);
    }, 300);
  };

  // Validate selected address
  const validateAddress = async (address, placeId) => {
    setIsValidating(true);
    try {
      const response = await axios.post(API_ENDPOINTS.ADDRESSES_VALIDATE, {
        address: address,
        placeId: placeId
      });

      if (response.data.success && response.data.data) {
        const validationData = response.data.data;
        setIsValid(true);
        setSelectedAddress({
          address: validationData.formattedAddress,
          latitude: validationData.latitude,
          longitude: validationData.longitude,
          placeId: validationData.placeId
        });

        // Update the input value to the formatted address
        setInputValue(validationData.formattedAddress);
        
        // Call onChange with formatted address
        if (onChange) {
          onChange({
            target: {
              name: id,
              value: validationData.formattedAddress
            }
          });
        }

        // Notify parent component of validation result
        if (onValidationChange) {
          onValidationChange({
            address: validationData.formattedAddress,
            latitude: validationData.latitude,
            longitude: validationData.longitude,
            placeId: validationData.placeId,
            isValid: true
          });
        }
      } else {
        setIsValid(false);
        setSelectedAddress(null);
        if (onValidationChange) {
          onValidationChange({ isValid: false });
        }
      }
    } catch (error) {
      console.error('Error validating address:', error);
      setIsValid(false);
      setSelectedAddress(null);
      if (onValidationChange) {
        onValidationChange({ isValid: false });
      }
    } finally {
      setIsValidating(false);
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    setInputValue(suggestion.description);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Validate the selected address
    validateAddress(suggestion.description, suggestion.placeId);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Try to validate current input
        if (inputValue.trim().length >= 2) {
          validateAddress(inputValue.trim());
        }
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[focusedIndex]);
        } else if (inputValue.trim().length >= 2) {
          validateAddress(inputValue.trim());
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Handle input blur
  const handleBlur = (e) => {
    // Delay to allow click on suggestion
    setTimeout(() => {
      setShowSuggestions(false);
      setFocusedIndex(-1);
    }, 200);
  };

  // Handle input focus
  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Scroll focused suggestion into view
  useEffect(() => {
    if (focusedIndex >= 0 && suggestionsRef.current) {
      const focusedElement = suggestionsRef.current.children[focusedIndex];
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedIndex]);

  const errorId = error ? `${id}-error` : undefined;
  const helpId = helpText ? `${id}-help` : undefined;
  const describedBy = [errorId, helpId].filter(Boolean).join(' ');

  return (
    <div className={`form-field ${className}`}>
      <label 
        htmlFor={id} 
        className="form-label"
      >
        {label}
        {required && (
          <span className="text-red-500 mr-1" aria-label="שדה חובה">
            *
          </span>
        )}
      </label>
      
      {helpText && (
        <div id={helpId} className="text-sm text-gray-600 mb-2">
          {helpText}
        </div>
      )}

      <div className="relative">
        <div className="relative">
          <input
            ref={inputRef}
            id={id}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onFocus={handleFocus}
            placeholder={placeholder}
            maxLength={500}
            disabled={disabled}
            className={`input-field pr-10 ${error ? 'border-red-500' : ''} ${isValid ? 'border-green-500' : ''}`}
            aria-describedby={describedBy || undefined}
            aria-invalid={error ? 'true' : 'false'}
            aria-required={required}
            aria-autocomplete="list"
            role="combobox"
            aria-expanded={showSuggestions}
            aria-controls={showSuggestions ? `${id}-suggestions` : undefined}
          />
          
          {/* Status icons */}
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            {isValidating && (
              <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
            )}
            {!isValidating && isValid && (
              <CheckCircle2 className="h-5 w-5 text-green-500" aria-label="כתובת מאומתת" />
            )}
            {!isValidating && !isValid && error && (
              <AlertCircle className="h-5 w-5 text-red-500" aria-label="שגיאה" />
            )}
            {!isValidating && !isValid && !error && inputValue && (
              <MapPin className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <ul
            ref={suggestionsRef}
            id={`${id}-suggestions`}
            role="listbox"
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto"
            aria-label="הצעות כתובות"
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.placeId || index}
                role="option"
                aria-selected={focusedIndex === index}
                className={`px-4 py-3 cursor-pointer hover:bg-orange-50 transition-colors ${
                  focusedIndex === index ? 'bg-orange-100' : ''
                } ${index === 0 ? 'rounded-t-lg' : ''} ${index === suggestions.length - 1 ? 'rounded-b-lg' : ''}`}
                onClick={() => handleSelectSuggestion(suggestion)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectSuggestion(suggestion);
                  }
                }}
                onMouseEnter={() => setFocusedIndex(index)}
                tabIndex={0}
              >
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-orange-500 ml-2 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-brown-900">
                      {suggestion.structuredFormatting?.main_text || suggestion.description}
                    </div>
                    {suggestion.structuredFormatting?.secondary_text && (
                      <div className="text-sm text-gray-600 mt-1">
                        {suggestion.structuredFormatting.secondary_text}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Loading indicator */}
        {isLoading && !showSuggestions && (
          <div className="absolute left-3 top-full mt-1 text-sm text-gray-500">
            מחפש כתובות...
          </div>
        )}
      </div>

      {error && (
        <div 
          id={errorId} 
          className="form-error" 
          role="alert"
          aria-live="polite"
        >
          {error}
        </div>
      )}

      {isValid && selectedAddress && (
        <div className="text-sm text-green-600 mt-1 flex items-center">
          <CheckCircle2 className="h-4 w-4 ml-1" />
          כתובת מאומתת
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;

